import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function ReviewCarousel({ reviews }) {
  const [openReview, setOpenReview] = useState(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [overflowing, setOverflowing] = useState({});
  const trackRef = useRef(null);
  const textRefs = useRef({});

  const visible = (reviews || [])
    .filter((r) => r.visible !== false && r.review_text)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [visible.length]);

  // The website decides — from the rendered 3-line clamp — whether a review actually
  // overflows. The owner never writes a separate short version.
  const measureOverflow = () => {
    const next = {};
    visible.forEach((r) => {
      const key = r.id || r.reviewer;
      const el = textRefs.current[key];
      if (el) next[key] = el.scrollHeight > el.clientHeight + 2;
    });
    setOverflowing(next);
  };

  useEffect(() => {
    measureOverflow();
    window.addEventListener("resize", measureOverflow);
    return () => window.removeEventListener("resize", measureOverflow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.map((r) => r.review_text).join("|")]);

  if (visible.length === 0) return null;

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.max(320, el.clientWidth * 0.7);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <>
      <div className="review-viewport" data-testid="review-carousel">
        <button
          type="button"
          className="review-arrow prev"
          onClick={() => scrollBy(-1)}
          disabled={!canPrev}
          data-testid="review-scroll-prev"
          aria-label="Previous reviews"
        >
          <ChevronLeft size={22} />
        </button>

        <div className="review-track" ref={trackRef}>
          {visible.map((r, i) => {
            const key = r.id || r.reviewer;
            const hasMore = !!overflowing[key];
            return (
              <article
                className="review-card"
                key={key}
                data-testid={`review-card-${i}`}
              >
                <div className="review-stars" aria-hidden>
                  {"★".repeat(r.rating || 5)}
                </div>
                <p
                  className="review-text"
                  ref={(el) => {
                    textRefs.current[key] = el;
                  }}
                >
                  {r.review_text}
                </p>
                <div className="review-foot">
                  <span>— {r.reviewer}</span>
                  {hasMore && (
                    <button
                      type="button"
                      onClick={() => setOpenReview(r)}
                      data-testid={`review-more-${i}`}
                    >
                      MORE
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <button
          type="button"
          className="review-arrow next"
          onClick={() => scrollBy(1)}
          disabled={!canNext}
          data-testid="review-scroll-next"
          aria-label="Next reviews"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {openReview && (
        <div
          className="review-modal"
          onClick={() => setOpenReview(null)}
          data-testid="review-modal"
          role="dialog"
          aria-modal="true"
        >
          <article onClick={(e) => e.stopPropagation()}>
            <button
              className="close"
              type="button"
              onClick={() => setOpenReview(null)}
              data-testid="review-modal-close"
              aria-label="Close review"
            >
              <X size={18} />
            </button>
            <p className="eyebrow">GENUINE GOOGLE REVIEW</p>
            <div className="review-stars">
              {"★".repeat(openReview.rating || 5)}
            </div>
            <p className="review-full">{openReview.review_text}</p>
            <span className="reviewer">
              — {openReview.reviewer}
              {openReview.review_date ? ` · ${openReview.review_date}` : ""}
            </span>
          </article>
        </div>
      )}
    </>
  );
}
